import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { DynamicFormComponent } from 'src/app/components/dynamic-form/dynamic-form.component';
import { LoadingIconComponent } from 'src/app/components/loading-icon/loading-icon.component';
import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { ProcessService } from 'src/app/services/process.service';
import { ToastService } from 'src/app/services/toast.service';
import { FormControlType, INDICATOR_TYPE, SCREEN_SIZE } from 'src/app/shared/enums';
import { IFormConfig, IFormItem } from 'src/app/shared/interfaces/IForm';
import { formatDecimalWithSignificantDigits } from 'src/app/shared/utils';

const MAX_SIGNIFICANT_DIGITS = 15;

interface IndicatorItem {
  key: string;
  label: string;
  type: INDICATOR_TYPE;
  inputType?: string | null;
  hiddenMode?: 'adminOnly' | 'nonAdminOnly';
  validator?: Array<{
    errorType: string;
    errorMessage: string;
    params?: Record<string, any>;
  }>;
}

@Component({
  selector: 'app-process-indicator-detail',
  imports: [DynamicFormComponent, IconModule, MaterialModule, TranslateModule, CommonModule, LoadingIconComponent],
  templateUrl: './process-indicator-detail.component.html',
  styleUrls: ['./process-indicator-detail.component.scss']
})
export class ProcessIndicatorDetailComponent implements OnInit {
  @Input() process: any;
  @Input() allocationTypeId: number;
  @Input() type: INDICATOR_TYPE;
  formConfigs: IFormConfig;
  formGroup: FormGroup;
  isLoading: boolean;
  private loadedIndicatorValues: Record<string, { display: string; value: number }> = {};

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private translateService: TranslateService,
    private toastService: ToastService,
    private processService: ProcessService) { }

  ngOnInit(): void {
    this.initForm();
    this.getProcessIndicator();
    this.handleAutoGenerateUniqueId();
  }

  getProcessIndicator() {
    this.processService
      .getProcessIndicatorByProcessIdAndAllocationTypeId(this.process.processId, this.allocationTypeId)
      .subscribe((res) => {
        if (res) {
          const indicator = this.isAdmin ? res : this.formatIndicatorForUserDisplay(res);
          this.formGroup.patchValue(this.toPlainDisplayValues(indicator));
        }
      });

    if (this.process.data) {
      const { datasetMainName, id } = this.process.data;
      this.formGroup.patchValue({
        processName: datasetMainName,
        processId: id
      });
    }

    this.formGroup.patchValue({
      allocationTypeId: this.allocationTypeId
    });
  }

  private formatIndicatorForUserDisplay(indicator: Record<string, any>): Record<string, any> {
    const formatted = { ...indicator };
    for (const item of INDICATORS) {
      if (item.inputType !== undefined) 
        continue;
      const value = formatted[item.key];
      if (value == null || value === '') 
        continue;
      const numeric = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(numeric)) 
        continue;
      formatted[item.key] = formatDecimalWithSignificantDigits(numeric);
    }
    return formatted;
  }

  private toPlainDisplayValues(indicator: Record<string, any>): Record<string, any> {
    const display = { ...indicator };
    this.loadedIndicatorValues = {};
    for (const item of INDICATORS) {
      if (item.inputType !== undefined) 
        continue;
      const value = display[item.key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        const text = this.toPlainNumberString(value);
        display[item.key] = text;
        this.loadedIndicatorValues[item.key] = { display: text, value };
      }
    }
    return display;
  }

  private toNumericValues(formValue: Record<string, any>): Record<string, any> {
    const payload = { ...formValue };
    for (const item of INDICATORS) {
      if (item.inputType !== undefined) 
        continue;
      const value = payload[item.key];
      if (value == null || value === '') {
        payload[item.key] = null;
      } else if (this.loadedIndicatorValues[item.key]?.display === value?.toString()) {
        payload[item.key] = this.loadedIndicatorValues[item.key].value;
      } else if (typeof value === 'string') {
        payload[item.key] = Number(value);
      }
    }
    return payload;
  }

  private maxSignificantDigitsValidator(key: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value == null || value === '') 
        return null;

      if (this.loadedIndicatorValues[key]?.display === value.toString()) 
        return null;

      const text = typeof value === 'number' ? this.toPlainNumberString(value) : value.toString().trim();
      const significantDigits = text.replace(/^[-+]/, '').replace('.', '').replace(/^0+/, '').replace(/0+$/, '').length;

      return significantDigits > MAX_SIGNIFICANT_DIGITS ? { maxSignificantDigits: true } : null;
    };
  }

  private toPlainNumberString(value: number): string {
    const text = value.toString();
    if (!/e/i.test(text)) return text;

    const [mantissa, exponentText] = text.toLowerCase().split('e');
    const exponent = Number(exponentText);
    const negative = mantissa.startsWith('-');
    const [intPart, fracPart = ''] = mantissa.replace('-', '').split('.');
    const digits = intPart + fracPart;
    const pointIndex = intPart.length + exponent;

    let result: string;
    if (pointIndex <= 0) {
      result = `0.${'0'.repeat(-pointIndex)}${digits}`;
    } else if (pointIndex >= digits.length) {
      result = `${digits}${'0'.repeat(pointIndex - digits.length)}`;
    } else {
      result = `${digits.slice(0, pointIndex)}.${digits.slice(pointIndex)}`;
    }
    return `${negative ? '-' : ''}${result}`;
  }

  private buildValidators(item: IndicatorItem) {
    const defaultValidators = item.inputType === null ? [] : [
      { errorType: 'pattern', errorMessage: 'must be a number' },
      { errorType: 'min', errorMessage: `${item.label} must be greater than`, params: { min: -9999999999 }},
      { errorType: 'max', errorMessage: `${item.label} must be less than`, params: { max: 9999999999 }},
      { errorType: 'maxSignificantDigits', errorMessage: 'maximum significant digits', params: { max: MAX_SIGNIFICANT_DIGITS } }
    ];

    return [...defaultValidators, ...(item.validator ?? [])];
  }

  private buildInputConfig(item: IndicatorItem) {
    if (item.key === 'efUniqueId' || item.key === 'recipeUniqueId') {
      return {
        type: 'text',
        readonly: true,
        class: 'readonly-input'
      };
    }
    const inputType = item.inputType === undefined ? 'number' : item.inputType;

    return {
      ...(inputType ? { type: inputType } : {}),
      readonly: !this.isAdmin
    };
  }

  private buildHidden(item: IndicatorItem): (() => boolean) | undefined {
    switch (item.hiddenMode) {
      case 'adminOnly':
        return () => !this.isAdmin;
      case 'nonAdminOnly':
        return () => this.isAdmin;
      default:
        return undefined;
    }
  }

  initForm() {
    this.formGroup = this.formBuilder.group({
      id: [null],
      processId: [null, Validators.required],
      allocationTypeId: [{ value: null, disabled: true }, Validators.required],
      efAp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efGwptotal: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efGwpbiogenic: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efGwpfossil: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efGwpluluc: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efGwpexcl: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efGwppeat: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efEtpfwPart1: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efEtpfwinorg: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efEtpfworgPart1: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efPm: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efEpmarine: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efEpfreshwater: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efEpterrestrial: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efHtpc: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efHtpcinorg: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efHtpcorg: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efHtpnc: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efHtpncinorg: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efHtpncorg: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efIrp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efSqp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efOdp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efPocp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efAdpfossil: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efAdpminerals: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efWdp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      efClass: [null, [Validators.maxLength(2)]],
      efSubClass: [null, [Validators.maxLength(2)]],
      efSubgroup: [null, [Validators.maxLength(3)]],
      efSubgroup2: [null, [Validators.maxLength(3)]],
      efBoundaries: [null, [Validators.maxLength(1)]],
      efCountryCode: [null, [Validators.maxLength(3)]],
      efStateCode: [null, [Validators.maxLength(2)]],
      efTier: [null, [Validators.maxLength(2)]],
      efAllocation: [null, [Validators.maxLength(3)]],
      efImpactMethod: [null, [Validators.maxLength(2)]],
      efUniqueId: [null],
      recipeGwpTot: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeGwpExcl: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeGwpLuc: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeGwpPeat: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeOdp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeIrp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeHofp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipePmfp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeEofp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeTap: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeFep: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeMep: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeTetp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeFetp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeMetp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeHtpc: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeHtpnc: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeLutot: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeLutr: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeLop: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeSop: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeFfp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeWcp: [null, [Validators.min(-9999999999), Validators.max(9999999999)]],
      recipeClass: [null, [Validators.maxLength(2)]],
      recipeSubClass: [null, [Validators.maxLength(2)]],
      recipeSubgroup: [null, [Validators.maxLength(3)]],
      recipeSubgroup2: [null, [Validators.maxLength(3)]],
      recipeBoundaries: [null, [Validators.maxLength(1)]],
      recipeCountryCode: [null, [Validators.maxLength(3)]],
      recipeStateCode: [null, [Validators.maxLength(2)]],
      recipeTier: [null, [Validators.maxLength(2)]],
      recipeAllocation: [null, [Validators.maxLength(3)]],
      recipeImpactMethod: [null, [Validators.maxLength(2)]],
      recipeUniqueId: [null]
    });

    for (const item of INDICATORS) {
      if (item.inputType !== undefined) 
        continue;
      this.formGroup.get(item.key)?.addValidators(this.maxSignificantDigitsValidator(item.key));
    }

    const appearance: IFormItem['appearance'] = this.isAdmin ? 'outline' : 'fill';
    this.formConfigs = {
      id: 'processIndicatorDetail',
      items: [
        ...INDICATORS.filter(({ type }) => type === this.type).map((item) => ({
          key: item.key,
          label: item.label,
          layoutConfig: [
            { column: 12 },
            { column: 6, screenSize: SCREEN_SIZE.SMALL },
            { column: 3, screenSize: SCREEN_SIZE.LARGE }
          ],
          controlType: FormControlType.INPUT,
          appearance: appearance,
          inputConfig: this.buildInputConfig(item),
          validator: this.buildValidators(item),
          hidden: this.buildHidden(item)
        }))
      ]
    };
  }

  onSubmit() {
    if (this.formGroup.invalid) return this.formGroup.markAllAsTouched();
    this.isLoading = true;
    this.processService
        .addOrUpdateProcessIndicator(this.toNumericValues(this.formGroup.getRawValue()))
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe({
          next: (res) => {
            if (res) {
              this.toastService.success(this.translateService.instant('update successfully'));
              this.formGroup.patchValue({ id: res });
            }
            this.isLoading = false;
          },
          error: ({ error }) => {
            console.error(error);
            this.isLoading = false;
          }
    });
  }

  get isAdmin(): boolean {
    return this.router.url.startsWith('/admin');
  }

  private buildEfUniqueId(): string {
    return [
      this.formGroup.get('efClass')?.value,
      this.formGroup.get('efSubClass')?.value,
      this.formGroup.get('efSubgroup')?.value,
      this.formGroup.get('efSubgroup2')?.value,
      this.formGroup.get('efBoundaries')?.value,
      this.formGroup.get('efCountryCode')?.value,
      this.formGroup.get('efStateCode')?.value,
      this.formGroup.get('efTier')?.value,
      this.formGroup.get('efAllocation')?.value,
      this.formGroup.get('efImpactMethod')?.value
    ]
      .filter((x) => x)
      .join('-');
  }

  private buildRecipeUniqueId(): string {
    return [
      this.formGroup.get('recipeClass')?.value,
      this.formGroup.get('recipeSubClass')?.value,
      this.formGroup.get('recipeSubgroup')?.value,
      this.formGroup.get('recipeSubgroup2')?.value,
      this.formGroup.get('recipeBoundaries')?.value,
      this.formGroup.get('recipeCountryCode')?.value,
      this.formGroup.get('recipeStateCode')?.value,
      this.formGroup.get('recipeTier')?.value,
      this.formGroup.get('recipeAllocation')?.value,
      this.formGroup.get('recipeImpactMethod')?.value
    ].filter((x) => x)
     .join('-');
  }

  private handleAutoGenerateUniqueId() {
    this.formGroup.valueChanges.subscribe(() => {
      this.formGroup.patchValue({ efUniqueId: this.buildEfUniqueId() }, { emitEvent: false });

      this.formGroup.patchValue({ recipeUniqueId: this.buildRecipeUniqueId() }, { emitEvent: false });
    });
  }
}

export const INDICATORS: IndicatorItem[] = [
  {
    key: 'efAp',
    label: 'efAp',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efGwptotal',
    label: 'efGwptotal',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efGwpbiogenic',
    label: 'efGwpbiogenic',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efGwpfossil',
    label: 'efGwpfossil',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efGwpluluc',
    label: 'efGwpluluc',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efGwpexcl',
    label: 'efGwpexcl',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efGwppeat',
    label: 'efGwppeat',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efEtpfwPart1',
    label: 'efEtpfwPart1',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efEtpfwinorg',
    label: 'efEtpfwinorg',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efEtpfworgPart1',
    label: 'efEtpfworgPart1',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efPm',
    label: 'efPm',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efEpmarine',
    label: 'efEpmarine',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efEpfreshwater',
    label: 'efEpfreshwater',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efEpterrestrial',
    label: 'efEpterrestrial',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efHtpc',
    label: 'efHtpc',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efHtpcinorg',
    label: 'efHtpcinorg',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efHtpcorg',
    label: 'efHtpcorg',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efHtpnc',
    label: 'efHtpnc',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efHtpncinorg',
    label: 'efHtpncinorg',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efHtpncorg',
    label: 'efHtpncorg',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efIrp',
    label: 'efIrp',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efSqp',
    label: 'efSqp',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efOdp',
    label: 'efOdp',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efPocp',
    label: 'efPocp',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efAdpfossil',
    label: 'efAdpfossil',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efAdpminerals',
    label: 'efAdpminerals',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efWdp',
    label: 'efWdp',
    type: INDICATOR_TYPE.EF31
  },
  {
    key: 'efClass',
    label: 'class',
    type: INDICATOR_TYPE.EF31,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'class length must not exceed',
        params: { length: 2 }
      }
    ]
  },
  {
    key: 'efSubClass',
    label: 'subclass',
    type: INDICATOR_TYPE.EF31,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'subclass length must not exceed',
        params: { length: 2 }
      }
    ]
  },
  {
    key: 'efSubgroup',
    label: 'subgroup',
    type: INDICATOR_TYPE.EF31,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'subgroup length must not exceed',
        params: { length: 3 }
      }
    ]
  },
  {
    key: 'efSubgroup2',
    label: 'subgroup2',
    type: INDICATOR_TYPE.EF31,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'subgroup2 length must not exceed',
        params: { length: 3 }
      }
    ]
  },
  {
    key: 'efBoundaries',
    label: 'boundaries',
    type: INDICATOR_TYPE.EF31,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'boundaries length must not exceed',
        params: { length: 1 }
      }
    ]
  },
  {
    key: 'efCountryCode',
    label: 'country code',
    type: INDICATOR_TYPE.EF31,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'country code length must not exceed',
        params: { length: 3 }
      }
    ]
  },
  {
    key: 'efStateCode',
    label: 'state code',
    type: INDICATOR_TYPE.EF31,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'state code length must not exceed',
        params: { length: 2 }
      }
    ]
  },
  {
    key: 'efTier',
    label: 'tier',
    hiddenMode: 'adminOnly',
    type: INDICATOR_TYPE.EF31,
    inputType: null,
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'tier length must not exceed',
        params: { length: 2 }
      }
    ]
  },
  {
    key: 'efAllocation',
    label: 'allocation',
    type: INDICATOR_TYPE.EF31,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'allocation length must not exceed',
        params: { length: 3 }
      }
    ]
  },
  {
    key: 'efImpactMethod',
    label: 'impactMethod',
    type: INDICATOR_TYPE.EF31,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'impactMethod length must not exceed',
        params: { length: 2 }
      }
    ]
  },
  {
    key: 'efUniqueId',
    label: 'unique code',
    type: INDICATOR_TYPE.EF31,
    inputType: null
  },
  {
    key: 'recipeGwpTot',
    label: 'recipeGwpTot',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeGwpExcl',
    label: 'recipeGwpExcl',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeGwpLuc',
    label: 'recipeGwpLuc',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeGwpPeat',
    label: 'recipeGwpPeat',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeOdp',
    label: 'recipeOdp',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeIrp',
    label: 'recipeIrp',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeHofp',
    label: 'recipeHofp',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipePmfp',
    label: 'recipePmfp',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeEofp',
    label: 'recipeEofp',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeTap',
    label: 'recipeTap',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeFep',
    label: 'recipeFep',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeMep',
    label: 'recipeMep',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeTetp',
    label: 'recipeTetp',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeFetp',
    label: 'recipeFetp',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeMetp',
    label: 'recipeMetp',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeHtpc',
    label: 'recipeHtpc',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeHtpnc',
    label: 'recipeHtpnc',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeLutot',
    label: 'recipeLutot',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeLutr',
    label: 'recipeLutr',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeLop',
    label: 'recipeLop',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeSop',
    label: 'recipeSop',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeFfp',
    label: 'recipeFfp',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeWcp',
    label: 'recipeWcp',
    type: INDICATOR_TYPE.ReCiPe
  },
  {
    key: 'recipeClass',
    label: 'class',
    type: INDICATOR_TYPE.ReCiPe,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'class length must not exceed',
        params: { length: 2 }
      }
    ]
  },
  {
    key: 'recipeSubClass',
    label: 'subclass',
    type: INDICATOR_TYPE.ReCiPe,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'subclass length must not exceed',
        params: { length: 2 }
      }
    ]
  },
  {
    key: 'recipeSubgroup',
    label: 'subgroup',
    type: INDICATOR_TYPE.ReCiPe,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'subgroup length must not exceed',
        params: { length: 3 }
      }
    ]
  },
  {
    key: 'recipeSubgroup2',
    label: 'subgroup2',
    type: INDICATOR_TYPE.ReCiPe,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'subgroup2 length must not exceed',
        params: { length: 3 }
      }
    ]
  },
  {
    key: 'recipeBoundaries',
    label: 'boundaries',
    type: INDICATOR_TYPE.ReCiPe,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'boundaries length must not exceed',
        params: { length: 1 }
      }
    ]
  },
  {
    key: 'recipeCountryCode',
    label: 'country code',
    type: INDICATOR_TYPE.ReCiPe,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'country code length must not exceed',
        params: { length: 3 }
      }
    ]
  },
  {
    key: 'recipeStateCode',
    label: 'state code',
    type: INDICATOR_TYPE.ReCiPe,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'state code length must not exceed',
        params: { length: 2 }
      }
    ]
  },
  {
    key: 'recipeTier',
    label: 'tier',
    type: INDICATOR_TYPE.ReCiPe,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'tier length must not exceed',
        params: { length: 2 }
      }
    ]
  },
  {
    key: 'recipeAllocation',
    label: 'allocation',
    type: INDICATOR_TYPE.ReCiPe,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'allocation length must not exceed',
        params: { length: 3 }
      }
    ]
  },
  {
    key: 'recipeImpactMethod',
    label: 'impactMethod',
    type: INDICATOR_TYPE.ReCiPe,
    inputType: null,
    hiddenMode: 'adminOnly',
    validator: [
      {
        errorType: 'maxlength',
        errorMessage: 'impactMethod length must not exceed',
        params: { length: 2 }
      }
    ]
  },
  {
    key: 'recipeUniqueId',
    label: 'unique code',
    type: INDICATOR_TYPE.ReCiPe,
    inputType: null
  }
];
